/**
 * Sistema de colores corporativo UNIFICADO para badges/chips
 *
 * Fuente única de verdad para todos los features (peticiones, incidencias,
 * traspasos, recogidas, ordenes). Cada feature importa de aquí.
 *
 * DECISION (unificación jul 2026): la paleta toma los TONOS del feature ordenes
 * (azul/rojo/verde/ámbar "profesionales") pero renderizados en estilo pastel:
 * fondo claro + texto oscurecido (accesible WCAG AA ≥4.5:1) + borde con el tono
 * original al 25%. Así el fondo/borde conservan la identidad de color de ordenes
 * y el texto queda legible.
 */

export const BADGE_COLORS = {
  // Rojo (base #e74c3c) - Crítico / Requiere atención / En preparación
  RED: {
    bg: "#fdece9",
    text: "#c0392b",
    border: "#e74c3c",
  },

  // Ámbar (base #f39c12) - Advertencia / En proceso / En revisión
  AMBER: {
    bg: "#fef4e2",
    text: "#8a5a00",
    border: "#f39c12",
  },

  // Verde (base #27ae60) - Éxito / Completado / Preparado
  GREEN: {
    bg: "#e8f7ef",
    text: "#196f3d",
    border: "#27ae60",
  },

  // Azul (base #3498db) - Información / Flujo estándar / Sin preparar
  BLUE: {
    bg: "#e8f3fb",
    text: "#185a8a",
    border: "#3498db",
  },

  // Gris (base #95a5a6) - Neutral / Sin dato / Indefinido
  GRAY: {
    bg: "#f2f4f4",
    text: "#5a6668",
    border: "#95a5a6",
  },

  // Púrpura (base #9c27b0) - Identificación de categorías (sin semántica de estado)
  PURPLE: {
    bg: "#f3e5f5",
    text: "#6a1b9a",
    border: "#9c27b0",
  },

  // Teal (base #009688) - Identificación de categorías (sin semántica de estado)
  TEAL: {
    bg: "#e0f2f1",
    text: "#00695c",
    border: "#009688",
  },

  // Índigo (base #3f51b5) - Identificación de categorías (sin semántica de estado)
  INDIGO: {
    bg: "#e8eaf6",
    text: "#283593",
    border: "#3f51b5",
  },

  // Rosa (base #e91e63) - Identificación de categorías (sin semántica de estado)
  PINK: {
    bg: "#fce4ec",
    text: "#ad1457",
    border: "#e91e63",
  },
};

/**
 * Traducción de severidad semántica de MUI (warning/success/error/info/default)
 * al color corporativo equivalente. Útil para migrar código que usaba color="...".
 */
export const BADGE_COLOR_BY_SEVERITY = {
  error: BADGE_COLORS.RED,
  warning: BADGE_COLORS.AMBER,
  success: BADGE_COLORS.GREEN,
  info: BADGE_COLORS.BLUE,
  default: BADGE_COLORS.GRAY,
};

/**
 * Colores diferenciados por almacén/nave para identificación visual rápida.
 * Set independiente (necesita 6 tonos distintos), ya en pastel accesible.
 */
export const ALMACEN_COLORS = {
  "01": { bg: "#e8f3fb", text: "#185a8a", border: "#3498db" }, // Azul
  "02": { bg: "#e8f7ef", text: "#196f3d", border: "#27ae60" }, // Verde
  "03": { bg: "#fdece9", text: "#c0392b", border: "#e74c3c" }, // Rojo (prioritario)
  "04": { bg: "#fef4e2", text: "#8a5a00", border: "#f39c12" }, // Ámbar
  "05": { bg: "#f3e5f5", text: "#6a1b9a", border: "#9c27b0" }, // Púrpura
  "06": { bg: "#e0f2f1", text: "#00695c", border: "#009688" }, // Teal
  DEFAULT: BADGE_COLORS.GRAY,
};

/**
 * Obtiene el color para un almacén específico
 * @param {string} codigoAlmacen - Código del almacén (ej: "01", "02", "03")
 * @returns {Object} Objeto de color
 */
export const getAlmacenColor = (codigoAlmacen) => {
  return ALMACEN_COLORS[codigoAlmacen] || ALMACEN_COLORS.DEFAULT;
};

/**
 * Genera estilos consistentes de badge para la prop sx de un Chip
 * @param {Object} color - Objeto de color de BADGE_COLORS
 * @param {Object} options - Opciones adicionales (height, fontSize, withBorder)
 * @returns {Object} Objeto de estilos para sx prop
 */
export const getBadgeStyles = (color, options = {}) => {
  const { height = 24, fontSize = 11, withBorder = true } = options;

  return {
    height,
    fontSize,
    fontWeight: 600,
    backgroundColor: color.bg,
    color: color.text,
    ...(withBorder && { border: `1px solid ${color.border}40` }), // 40 = 25% opacity
  };
};
